import dayjs from 'dayjs'
import Big from 'big.js'

const formatFerryCrossing = (crossing, config) => {
  const labels = config?.ferry?.labels || {}
  const vehicleResourceCodesExpanded =
    config?.ferry?.options?.vehicleResourceCodesExpanded

  const {
    arrivalDate,
    arrivalTime,
    departureDate,
    departureTime,
    departurePriceData,
    passengerPriceLines,
    vehiclePriceLines,
    vehicles,
    routeCode,
    shipCode,
    passengers,
    addOnPriceLines,
    addOns,
    transportationTitle,
    code,
  } = crossing
  const routeData = Object.values(config?.ferry?.options?.routes || {})
    .flat()
    .find(route => route.code === routeCode)

  const formattedAddOns =
    addOns?.map(addOn => {
      const pricing =
        addOnPriceLines?.find(
          ({ resourceCode }) => resourceCode === addOn.resourceCode,
        ) || null

      return {
        resourceCode: addOn.resourceCode,
        ticketType: addOn.ticketType,
        amount: addOn.number ? +addOn.number : addOn.amount ? +addOn.amount : 1,
        price: pricing && pricing?.price ? +pricing.price : '',
        description: pricing?.description,
      }
    }) || []

  const crossingBasePrice = vehiclePriceLines?.[0]?.price
    ? +vehiclePriceLines[0].price
    : passengerPriceLines?.[0]?.price
    ? +passengerPriceLines[0].price
    : null

  return {
    addOns: formattedAddOns,
    arrivalDate,
    arrivalTime,
    departureDate,
    departureTime,
    departureDateTime: dayjs.tz(`${departureDate} ${departureTime}`).format(),
    arrivalDateTime: dayjs.tz(`${arrivalDate} ${arrivalTime}`).format(),
    guestCount: passengers?.length || 0,
    passengers,
    passengerPriceLines,
    priceData: departurePriceData,
    routeCode,
    routeDescription: routeData?.description || '',
    shipCode,
    transportationTitle,
    shipName:
      config?.ferry?.options?.ships?.find(ship => ship.value === shipCode)
        ?.label || '',
    vehiclePriceLines,
    vehicles: vehicles?.length
      ? vehicles?.map(vehicle => {
          const configData = vehicleResourceCodesExpanded?.find(
            resource => resource.resourceCode === vehicle.resourceCode,
          )
          const label = labels?.[configData?.groupName]
          return {
            ...vehicle,
            label,
          }
        })
      : null,
    crossingBasePrice,
    code,
  }
}

export const formatFerries = (ferry, config) => {
  const departures = ferry?.ferry?.departures || []
  const status = ferry?.status
  const isCancellable = ferry?.isCancellable
  const startDate = ferry?.ferry?.departures?.[0]?.departureDate
  const endDate =
    ferry?.ferry?.departures?.[1]?.departureDate ||
    ferry?.ferry?.departures?.[0]?.departureDate

  const fees = ferry?.ferry?.landBasedResources?.fees

  return {
    rph: ferry.rph,
    totalCost: ferry?.ferry?.reservationTotals?.totalPriceToPay,
    startDate,
    endDate,
    status,
    isCancellable,
    isEditable: !!ferry.isEditable,
    reservationTotals: ferry?.ferry?.reservationTotals,
    crossings: departures?.map(crossing =>
      formatFerryCrossing(crossing, config),
    ),
    coupon: ferry?.ferry?.coupon || null,
    applicableLoyalty: ferry?.ferry?.applicableLoyalty,
    cancellationProtection: ferry?.ferry?.cancellationProtection,
    fees,
    confirmationEmailAddress: ferry?.ferry?.confirmationEmailAddress || '',
    poNumber: ferry?.ferry?.poNumber || '',
  }
}

export const formatAttraction = attraction => {
  return attraction
}

export const formatAccommodation = accommodation => {
  const bookingReference = accommodation?.bookingReference
  const isCancellable = accommodation?.isCancellable
  const isEditable = accommodation?.isEditable
  const hotel = accommodation?.hotel
  const roomStay = hotel?.roomStays?.[0]
  const roomType = roomStay?.roomTypes?.[0]
  const hotelCode = roomStay?.basicPropertyInfo?.hotelCode
  const resourceCode = roomType?.content?.bookingMatrix?.[0]?.bookitResourceId
  const bookitProductId = roomType?.content?.bookingMatrix?.[0]?.bookitProductId
  const startDate = roomStay?.dateStart
  const endDate = roomStay?.dateEnd
  const hotelName = roomStay?.basicPropertyInfo?.hotelName
  const roomName =
    roomType?.roomDescription?.name || roomType?.content?.roomDescription?.name
  const numberOfUnits = roomType?.numberOfUnits
  const adults = roomStay?.adults
  const children = roomStay?.children
  const numberOfGuests = (adults || 0) + (children || 0)
  const status = accommodation?.status
  const coupon = accommodation?.coupon || null

  return {
    rph: accommodation.rph || '',
    bookingReference,
    isCancellable,
    isEditable,
    bookitProductId,
    hotelCode,
    resourceCode,
    startDate,
    endDate,
    hotelName,
    roomName,
    numberOfUnits,
    adults,
    children,
    numberOfGuests,
    status,
    id: roomType?.roomTypeCode,
    key: `${roomType?.roomTypeCode}-${startDate}-${endDate}`,
    coupon,
    hotelProvider: hotel?.hotelProvider,
    comments: hotel?.comments,
    chainCode: roomStay?.chainCode,
  }
}

const reducePostCartResponse = (data, config) => {
  const items = data?.itineraryInfo?.reservationItems || []
  const pricingItems = data?.itineraryPricing || []
  const reservationItems = items.map(item => {
    const cost = pricingItems.find(
      price => price.associatedItemRPH === item.rph,
    )?.cost
    return {
      ...item,
      cost,
    }
  })

  const reducedItems = reservationItems?.reduce(
    (obj, item) => {
      if (item?.ferry !== undefined) {
        if (item.ferry && item.ferry.departures.length > 0) {
          obj.ferries.push(formatFerries(item, config))
        }
        if (item.ferry.landBasedResources?.attractions.length > 0) {
          item.ferry.landBasedResources.attractions.forEach(attraction => {
            obj.attractions.push(formatAttraction(attraction))
          })
        }
        if (item.ferry.landBasedResources?.sundries?.length) {
          item.ferry.landBasedResources.sundries.forEach(donation => {
            obj.donations.push(donation)
          })
        }
      }
      if (item?.hotel) {
        obj.hotels.push(formatAccommodation(item))
      }

      return obj
    },
    {
      ferries: [],
      hotels: [],
      attractions: [],
      donations: [],
    },
  )

  reducedItems.hotels.forEach((hotel, i) => {
    const ferry = data?.itineraryInfo?.reservationItems.find(item => item.ferry)
    const landBasedResources = ferry?.ferry?.landBasedResources
    const accommodations = landBasedResources?.accommodations
    const selectedAccommodation = accommodations.find(
      accommodation => accommodation.resourceCode === hotel.resourceCode,
    )
    // https://verbinteractive.atlassian.net/browse/RED200-2136
    reducedItems.hotels[i].price =
      selectedAccommodation?.price &&
      selectedAccommodation?.amount &&
      new Big(
        selectedAccommodation.price / selectedAccommodation.amount,
      ).toNumber()
  })

  const paymentPlan = data?.guaranteesAccepted?.find(
    guarantee => guarantee.guaranteeTypeCode === 41,
  )?.guaranteeAcceptedViewExtensions?.paymentPlans?.[0]

  return {
    id: data?.id,
    itineraryId: data?.itineraryId,
    costTotals: data?.travelCost?.costTotals,
    paymentTotals: data?.travelCost?.paymentTotals,
    items: reducedItems,
    customerInfos: data.customerInfos,
    contactInfos: {},
    paymentPlan,
    applicableLoyalty: reducedItems?.ferries?.[0]?.applicableLoyalty || null,
  }
}

export default reducePostCartResponse
